import { useState, useEffect } from 'react';
import { onSnapshot, query, orderBy, updateDoc, doc, deleteDoc, getDoc, serverTimestamp, collection, addDoc } from 'firebase/firestore';
import { db, reportedReviewsCollection } from '../lib/firebase';
import toast from 'react-hot-toast';

interface Report {
  id: string;
  reviewId: string;
  reviewContent: string;
  extensionId: string;
  extensionName: string;
  reason: string;
  details?: string;
  status: 'pending' | 'resolved' | 'dismissed';
  createdAt: any;
}

export function useReports() {
  const [reports, setReports] = useState<Report[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const q = query(reportedReviewsCollection, orderBy('createdAt', 'desc'));
    
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const reportsData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as Report[];
      
      setReports(reportsData);
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const updateReportStatus = async (reportId: string, status: 'resolved' | 'dismissed') => {
    try {
      await updateDoc(doc(db, 'reportedReviews', reportId), { status });
      toast.success(`Report ${status}`);
    } catch (error) {
      console.error('Error updating report:', error);
      toast.error('Failed to update report');
    }
  };

  const deleteReport = async (reportId: string) => {
    if (!window.confirm('Are you sure you want to delete this report?')) {
      return;
    }

    try {
      await deleteDoc(doc(db, 'reportedReviews', reportId));
      toast.success('Report deleted successfully');
    } catch (error) {
      console.error('Error deleting report:', error);
      toast.error('Failed to delete report');
    }
  };

  const deleteReview = async (reviewId: string, extensionId: string) => {
    if (!window.confirm('Are you sure you want to delete this review?')) {
      return;
    }

    try {
      const reviewRef = doc(db, 'reviews', reviewId);
      const reviewDoc = await getDoc(reviewRef);
      const reviewData = reviewDoc.data();

      if (reviewData) {
        // Delete the review
        await deleteDoc(reviewRef);

        // Update extension stats
        const extensionRef = doc(db, 'extensions', extensionId);
        const extensionDoc = await getDoc(extensionRef);
        const extensionData = extensionDoc.data();

        if (extensionData) {
          const newTotalRatings = (extensionData.totalRatings || 0) - 1;
          const newTotalSum = (extensionData.totalRatingSum || 0) - reviewData.rating;
          const newAverage = newTotalRatings > 0 
            ? Math.round((newTotalSum / newTotalRatings) * 10) / 10 
            : 0;

          await updateDoc(extensionRef, {
            totalRatings: newTotalRatings,
            totalRatingSum: newTotalSum,
            averageRating: newAverage,
            updatedAt: serverTimestamp()
          });

          // Send notification to user
          await addDoc(collection(db, 'notifications'), {
            userId: reviewData.userId,
            type: 'REVIEW_DELETED',
            message: 'Your review has been removed by an administrator.',
            createdAt: serverTimestamp(),
            read: false
          });
        }

        toast.success('Review deleted successfully');
      }
    } catch (error) {
      console.error('Error deleting review:', error);
      toast.error('Failed to delete review');
    }
  };

  return { reports, loading, updateReportStatus, deleteReport, deleteReview };
} 